import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../store';

/**
 * Selects the currently highlighted human UUID from UI state.
 */
export const selectHighlightedHumanUuid = (state: RootState): string | null =>
  state.map3UI.highlightedHumanUuid;

/**
 * Selects all human relationships from data state.
 */
const selectHumanRelationships = (state: RootState) => state.map3Data.humanRelationships;

/**
 * Memoized selector that returns the allocations (objective relationships) for
 * the currently highlighted human.
 *
 * - If highlightedHumanUuid is null, returns an empty array.
 * - Filters humanRelationships where humanUuid matches AND targetType === 'objective'.
 * - Maps to { objectiveUuid, startDate?, endDate? }.
 */
export const selectHighlightedAllocations = createSelector(
  [selectHighlightedHumanUuid, selectHumanRelationships],
  (
    highlightedHumanUuid,
    humanRelationships,
  ): Array<{ objectiveUuid: string; startDate?: string; endDate?: string }> => {
    if (!highlightedHumanUuid) {
      return [];
    }

    return humanRelationships
      .filter(
        (rel) => rel.humanUuid === highlightedHumanUuid && rel.targetType === 'objective',
      )
      .map((rel) => ({
        objectiveUuid: rel.targetUuid,
        startDate: rel.startDate,
        endDate: rel.endDate,
      }));
  },
);
